import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PaymentPage from './PaymentPage';
import Logo from '../components/Logo';

export default function LandingPage() {
  // /pay?buy=1 opens the payment form, /pay?retrieve=1 the token lookup (links from the home page)
  const [searchParams] = useSearchParams();
  const [showPayment, setShowPayment] = useState(searchParams.get('buy') === '1');
  const [showRetrieve, setShowRetrieve] = useState(searchParams.get('retrieve') === '1' && searchParams.get('buy') !== '1');
  const [meterNumber, setMeterNumber] = useState('');
  const [retrievedToken, setRetrievedToken] = useState(null);
  const [retrieveError, setRetrieveError] = useState('');
  const [retrieving, setRetrieving] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleRetrieve = async () => {
    if (!meterNumber.trim()) return;
    setRetrieving(true);
    setRetrieveError('');
    setRetrievedToken(null);

    try {
      const res = await axios.get(`/api/payment/retrieve/${meterNumber}`);
      setRetrievedToken(res.data.token);
    } catch (err) {
      setRetrieveError(err.response?.data?.message || 'No tokens found for this meter');
    } finally {
      setRetrieving(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard?.writeText(retrievedToken.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    setShowRetrieve(false);
    setMeterNumber('');
    setRetrievedToken(null);
    setRetrieveError('');
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box", background: "#f1f5f9" }}>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(148,163,184,0.4); }
      `}</style>

      {/* ── HERO SECTION ── */}
      {!showPayment && !showRetrieve && (
        <div style={{ textAlign: "center", animation: "fadeIn 0.6s ease", maxWidth: 500, width: "100%" }}>

          {/* Logo */}
          <div style={{ marginBottom: 32 }}>
            <h1 aria-label="Smart Maji" style={{ margin: 0, display: "flex", justifyContent: "center" }}>
              <Logo height={64} tagline />
            </h1>
            <p style={{ color: "#475569", fontSize: 16, marginTop: 10 }}>
              Smart Water Meter Management & Payments
            </p>
          </div>

          {/* Status indicator */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,168,56,0.1)", border: "1px solid rgba(0,168,56,0.3)", borderRadius: 50, padding: "6px 16px", marginBottom: 40 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#00a838", animation: "pulse 2s infinite" }} />
            <span style={{ color: "#00a838", fontSize: 13, fontWeight: 600 }}>System Online</span>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <button
              onClick={() => setShowPayment(true)}
              style={{ background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 12, padding: "16px 32px", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 20px rgba(8,145,178,0.35)", display: "flex", alignItems: "center", gap: 10, transition: "transform 0.2s" }}
              onMouseOver={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseOut={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              Purchase Tokens
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.3)", borderRadius: 12, padding: "16px 32px", color: "#38bdf8", fontSize: 16, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, transition: "transform 0.2s" }}
              onMouseOver={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseOut={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              Login
            </button>
          </div>

          {/* Retrieve token link */}
          <button
            onClick={() => setShowRetrieve(true)}
            style={{ background: "none", border: "none", color: "#475569", fontSize: 13, cursor: "pointer", textDecoration: "underline", marginTop: 8 }}
          >
            Already paid? Retrieve your token →
          </button>

          <div>
            <button onClick={() => navigate('/services')}
              style={{ background: "none", border: "none", color: "#0891b2", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 14 }}>
              Own a property or water scheme? See our services →
            </button>
          </div>

          <p style={{ color: "#334155", fontSize: 12, marginTop: 40 }}>
            © 2026 Majic Water Systems · Powered by M-Pesa Daraja API
          </p>
        </div>
      )}

      {/* ── PAYMENT FORM ── */}
      {showPayment && (
        <div style={{ width: "100%", animation: "fadeIn 0.4s ease" }}>
          <div style={{ maxWidth: 440, margin: "0 auto 16px" }}>
            <button onClick={() => setShowPayment(false)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
              ← Back
            </button>
          </div>
          <PaymentPage />
        </div>
      )}

      {/* ── RETRIEVE TOKEN ── */}
      {showRetrieve && (
        <div style={{ width: "100%", maxWidth: 440, animation: "fadeIn 0.4s ease" }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 style={{ color: "#e2e8f0", fontWeight: 800, fontSize: 22, margin: 0 }}>
              Retrieve Your Token
            </h2>
            <p style={{ color: "#64748b", fontSize: 14, marginTop: 8 }}>
              Enter your meter number to get your most recent token
            </p>
          </div>

          {/* Card */}
          <div style={{ background: "rgba(15,23,42,0.92)", backdropFilter: "blur(20px)", borderRadius: 20, border: "1px solid rgba(56,189,248,0.15)", boxShadow: "0 20px 50px rgba(0,0,0,0.5)", padding: "28px 24px" }}>

            {!retrievedToken ? (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
                    Meter Number
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={meterNumber}
                      onChange={e => { setMeterNumber(e.target.value); setRetrieveError(''); }}
                      onKeyPress={e => e.key === 'Enter' && handleRetrieve()}
                      placeholder="e.g. 0292000016983"
                      style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: `1.5px solid ${retrieveError ? "#f87171" : "rgba(56,189,248,0.25)"}`, borderRadius: 10, padding: "13px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" }}
                      onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                      onBlur={e => e.target.style.border = `1.5px solid ${retrieveError ? "#f87171" : "rgba(56,189,248,0.25)"}`}
                    />
                  </div>
                  {retrieveError && (
                    <div style={{ color: "#f87171", fontSize: 12, marginTop: 6 }}>{retrieveError}</div>
                  )}
                </div>

                <button
                  onClick={handleRetrieve}
                  disabled={retrieving || !meterNumber.trim()}
                  style={{ width: "100%", background: retrieving ? "rgba(8,145,178,0.5)" : "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 12, padding: "14px", color: "#fff", fontSize: 15, fontWeight: 700, cursor: retrieving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                >
                  {retrieving ? (
                    <>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 1s linear infinite" }} />
                      Searching...
                    </>
                  ) : 'Find Token'}
                </button>
              </>
            ) : (
              <>
                {/* Token found */}
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "2px solid #10b981", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: 24, color: "#10b981", fontWeight: 700 }}>✓</div>
                  <div style={{ color: "#10b981", fontWeight: 700, fontSize: 16 }}>Token Found!</div>
                </div>

                {/* Token display */}
                <div style={{ background: "rgba(56,189,248,0.08)", border: "1.5px dashed #38bdf8", borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 20 }}>
                  <div style={{ color: "#94a3b8", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>Your Token</div>
                  <div style={{ fontFamily: "monospace", fontSize: "clamp(16px,5vw,24px)", fontWeight: 800, letterSpacing: 4, color: "#38bdf8", marginBottom: 12, wordBreak: "break-all" }}>
                    {retrievedToken.token}
                  </div>
                  <button
                    onClick={copyToken}
                    style={{ background: copied ? "#10b981" : "rgba(56,189,248,0.15)", color: copied ? "#fff" : "#38bdf8", border: "none", borderRadius: 8, padding: "6px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" }}
                  >
                    {copied ? "✓ Copied!" : "Copy Token"}
                  </button>
                </div>

                {/* Token details */}
                <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                  {[
                    ['Meter', meterNumber],
                    ['Amount Paid', `KES ${Number(retrievedToken.amount).toLocaleString()}`],
                    ...(Number(retrievedToken.units) > 0 ? [['Units', `${Number(retrievedToken.units).toLocaleString('en-KE', { maximumFractionDigits: 3 })} m³`]] : []),
                    ['Status', retrievedToken.used ? 'Already Used' : 'Not Yet Used'],
                    ['Date', new Date(retrievedToken.created_at).toLocaleString()]
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13 }}>
                      <span style={{ color: "#64748b" }}>{k}</span>
                      <span style={{ color: k === 'Amount Paid' ? "#38bdf8" : k === 'Status' ? (retrievedToken.used ? "#f87171" : "#10b981") : "#e2e8f0", fontWeight: k === 'Amount Paid' ? 700 : 400 }}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Warning if already used */}
                {retrievedToken.used && (
                  <div style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 10, padding: "12px 16px", color: "#fbbf24", fontSize: 13, marginBottom: 16, textAlign: "center" }}>
                    This token has already been used. Please make a new payment for more units.
                  </div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={reset} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#94a3b8", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                    ← Back
                  </button>
                  <button onClick={() => { reset(); setShowPayment(true); }} style={{ flex: 1, background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    Buy More Units
                  </button>
                </div>
              </>
            )}
          </div>

          <button onClick={reset} style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: "#475569", fontSize: 13, cursor: "pointer" }}>
            ← Back to Home
          </button>
        </div>
      )}
    </div>
  );
}